import { getFeatureFlags } from '../../web-client/queries/getFeatureFlags';
import type { MakeBridgeContext } from '../types';
import type { TBridgeMethod } from './types';

// ─── initFeatureFlags ─────────────────────────────────────────────────────────

type InitFeatureFlagsPayload = {
	config: unknown;
	user?: Record<string, unknown>;
};

type InitFeatureFlagsContext = MakeBridgeContext<
	'extensionDetails' | 'contextIds' | 'environment',
	'getContextToken' | 'tracing'
>;

/**
 * Initialises the Forge feature flags client SDK inside the iframe.
 *
 * Fetches a context token then POSTs to forge-outbound-proxy `/client/feature-flags`
 * with the user and config provided by the app.
 *
 * On success: returns the raw JSON response (feature flag assignments for the app).
 * On failure: logs to console and returns `{}` so the app degrades gracefully
 * rather than crashing.
 */
export const initFeatureFlags: TBridgeMethod<
	InitFeatureFlagsContext,
	InitFeatureFlagsPayload,
	object
> = async (context, payload) => {
	const {
		extensionDetails: { appId },
		contextIds,
		environment,
		services: { getContextToken, tracing },
	} = context;

	const contextToken = await getContextToken({ contextIdsOverride: contextIds });

	tracing.recordBridgeCall('getFeatureFlags - start');

	try {
		const resp = await getFeatureFlags(
			appId,
			payload.user,
			payload.config,
			environment,
			contextToken,
		);

		tracing.recordBridgeCall('getFeatureFlags - end');

		return resp;
	} catch (err) {
		tracing.recordBridgeCall('getFeatureFlags - end');
		// eslint-disable-next-line no-console
		console.error('There were errors while initialising feature flags client ', err);

		return {};
	}
};

// ─── trackFeatureFlagEvent ────────────────────────────────────────────────────

/**
 * No-op. Feature flag analytics are no longer tracked; the bridge method is kept
 * so existing apps calling it from the feature flags client SDK don't error.
 */
export const trackFeatureFlagEvent: TBridgeMethod<
	MakeBridgeContext,
	unknown,
	void
> = async () => {};
